import { ApolloClient, NormalizedCacheObject, from } from '@apollo/client';
import { createHttpLink } from '@apollo/client/link/http';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache } from '@apollo/client/cache';
import SerializingLink from 'apollo-link-serialize';
import QueueLink from 'apollo-link-queue';
import { persistCache } from 'apollo3-cache-persist';

import { GRAPHQL_HTTP_URI } from './constants';
import { tokenKeyForTree } from './services/auth.service';
import { SessionType } from './types/session.types';
import { createTrackerLink } from './utils/tracker.link';
import { createSessionExpiryLink } from './utils/session-expiry.link';
import { store } from './utils/storage';
import { IonicStorageWrapper } from './utils/ionic-storage-wrapper';
import { loadRecentScoredSessions, trimPersistedScores } from './utils/cache-persistence';
import { recordUserSession } from './utils/user-session.service';
import { createClientUpdateLink } from './utils/client-update.link';
import { initRemoteConfig } from './firebase';
import { gql } from '@apollo/client';
import { SessionReporter } from './utils/session.reporter';
import { ICalibrationAssignmentsPage } from './types/calibration-assignment.types';
import { mergeOffsetPage } from './helpers/pagination.helpers';
import { createOperationTimeoutLink, fetchGraphQLWithTimeout } from './helpers/graphql-timeout.helpers';
import { REPORT_CARD_AVAILABILITY_TYPE_POLICY } from './queries/report-card-access.queries';

// `reportsEveryAction: true` is a literal for the same reason as in RECORD_USER_SESSION: omitting it
// marks this bundle legacy and makes score_sync_require_current_client refuse current clients.
const INITIALIZE_USER_SESSION = gql`
  mutation InitializeUserSession($startedAt: Date!, $logRocketSessionId: String) {
    recordUserSession(startedAt: $startedAt, logRocketSessionId: $logRocketSessionId, reportsEveryAction: true) {
      loggedAt
    }
  }
`;

// GraphQL mutation to update user session with LogRocket session ID
const UPDATE_USER_SESSION_LOGROCKET = gql`
  mutation UpdateUserSessionLogRocket($logRocketSessionId: String!) {
    updateUserSessionLogRocket(logRocketSessionId: $logRocketSessionId) {
      _id
      userId
      startedAt
      logRocketSessionId
    }
  }
`;

const persistKeyForTree = (tree: SessionType) =>
  (tree === SessionType.PARENT ? 'apollo-cache-persist-parent' : 'apollo-cache-persist');

// Backstop only — trimPersistedScores keeps the snapshot far below this. Tripping it
// purges storage and pauses persistence for the rest of the session, silently.
const CACHE_PERSIST_MAX_SIZE = 25 * 1024 * 1024;

// Builds the ApolloClient for ONE session tree: its own isolated, separately persisted
// cache + an authLink fixed to that tree's token. Staff and parent never share a cache.
async function createApolloClient(tree: SessionType, user?: any) {
  // Fetch asynchronously: early operations use defaults and later ones receive activated limits.
  void initRemoteConfig();
  const serializingLink = new SerializingLink();
  // Only the staff app buffers offline scoring; the parent app has no queue.
  const queueLink = tree === SessionType.STAFF ? new QueueLink() : undefined;

  const httpLink = createHttpLink({ uri: GRAPHQL_HTTP_URI, fetch: fetchGraphQLWithTimeout });

  const authLink = setContext(async (_ctx, { headers: _headers }) => {
    const token = await store.get(tokenKeyForTree(tree));
    return { headers: { authorization: token ? `Bearer ${token}` : null } };
  });

  const storage = new IonicStorageWrapper(store);
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Accumulate offset pages under one entry (no `read` slice → read the full list)
          // so a page-0 refresh keeps loaded pages. Staff-only: the parent app never queries it.
          ...(tree === SessionType.STAFF ? {
            myCalibrationAssignments: {
              keyArgs: false,
              merge(
                existing: ICalibrationAssignmentsPage | undefined,
                incoming: ICalibrationAssignmentsPage,
                { args }: { args: { offset?: number; limit?: number } | null },
              ) {
                const offset = args && typeof args.offset === 'number' ? args.offset : 0;
                const limit = args && typeof args.limit === 'number' ? args.limit : undefined;
                return mergeOffsetPage(existing, incoming, offset, limit);
              },
            },
          } : {}),
        },
      },
      ...REPORT_CARD_AVAILABILITY_TYPE_POLICY,
    },
  });
  await loadRecentScoredSessions();
  await persistCache({
    cache,
    storage,
    key: persistKeyForTree(tree),
    persistenceMapper: trimPersistedScores,
    maxSize: CACHE_PERSIST_MAX_SIZE,
    debug: process.env.NODE_ENV === 'development',
  });

  // Innermost of the app links, so a post-refresh retry re-runs only authLink + http and
  // the tracker/queue see one outcome per operation. The client exists by the time it fails.
  const clientRef: { current?: ApolloClient<NormalizedCacheObject> } = {};
  const sessionExpiryLink = createSessionExpiryLink(tree, () => clientRef.current);

  const link = from([
    createTrackerLink(),
    ...(queueLink ? [queueLink] : []),
    serializingLink,
    // Outside sessionExpiryLink so it observes the settled outcome, not one a refresh retries.
    createClientUpdateLink(),
    sessionExpiryLink,
    createOperationTimeoutLink(),
    authLink.concat(httpLink)
  ]);

  const client = new ApolloClient({ cache, link, resolvers: {} });
  clientRef.current = client;

  if (user) {
    SessionReporter.setApolloClient(client);
    SessionReporter.identify({ _id: user._id, name: user.name, email: user.email });
  }

  // Seed the local user-session buffer (staff scores+edits, parent player edits) so
  // first-edit calls find a session. The server session-init mutation stays staff-only.
  if (user) {
    await recordUserSession(user);
  }
  if (tree === SessionType.STAFF && user) {
    try {
      // Let identify() generate a LogRocket session URL before the session-init mutation.
      await new Promise((resolve) => setTimeout(resolve, 500));
      await client.mutate({
        mutation: INITIALIZE_USER_SESSION,
        variables: { startedAt: new Date() }
      });
    } catch (error) {
      console.error('Failed to initialize user session on server:', error);
    }
  }

  return { client, queueLink };
}

// Export the mutation for use in other parts of the app
export { UPDATE_USER_SESSION_LOGROCKET, createApolloClient };
